import { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { useCreditSpend, useCreditSummary } from 'features/credits/hooks/use-credits';
import type { SubscriptionUsageMeter } from 'features/subscription/services/subscription-service';
import { useFreeAccess, useSeatPools } from 'features/subscription/hooks/use-seats';
import { UsageActivityCard } from 'features/usage/components/usage-activity-card';
import { UsageUnlimitedNotice } from 'features/usage/components/usage-credits';
import { UsageStateCard } from 'features/usage/components/usage-state-card';
import { UsageDailySection } from 'features/usage/components/usage-tables';
import { resetTime } from 'features/usage/components/usage-window-limit';
import { UsageWhoUsedCard } from 'features/usage/components/usage-who-used-card';
import { useTokenUsage, useWindowLimit } from 'features/usage/hooks/use-usage';
import { buildUsageViewModel } from 'features/usage/utils/build-usage-view-model';
import { seatUsageMeters } from 'features/usage/utils/seat-usage-meters';
import { formatNumber, formatWindow } from 'features/usage/utils/usage-format';
import type { TeamsPerson } from '../teams-mock-data';
import {
  mainWindow,
  meterPercent,
  meterRenewsOn,
  TeamsMonthCard,
  TeamsTodayCard,
} from './teams-usage-limit-cards';
import { pickTeamsUsageNotice, TeamsUsageNotice } from './teams-usage-notice';

const panelCardClassName = 'rounded-section border-border/70 shadow-none';

function periodElapsedShare(meter?: SubscriptionUsageMeter) {
  if (!meter?.periodStartUtc) return 0;
  const start = new Date(meter.periodStartUtc).getTime();
  const end = new Date(meter.periodEndUtc).getTime();
  if (!(end > start)) return 0;
  return Math.min(1, Math.max(0, (Date.now() - start) / (end - start)));
}

type TeamsUsagePanelBodyProps = {
  member: TeamsPerson;
  /** The plan the person holds now, or null when they have none. */
  planName: string | null;
  startDate: string;
  endDate: string;
};

/** Figma 10827:13793 — the person's limits, shared credits and usage for the picked range. */
export function TeamsUsagePanelBody({ member, planName, startDate, endDate }: TeamsUsagePanelBodyProps) {
  const { t } = useTranslation();
  const { isFreeAccess } = useFreeAccess();
  const userId = member.id;
  const hasCreditAccess = member.creditAccess ?? true;

  const usageQuery = useTokenUsage({ startDate, endDate, scope: 'user', userId });
  const windowLimitQuery = useWindowLimit(userId);
  const { data: seatPools, isLoading: isLoadingSeats } = useSeatPools(true, true);
  const { data: creditSummary } = useCreditSummary();
  const isSharedCreditsOn = !!creditSummary?.enabled;
  const { data: spend } = useCreditSpend(isSharedCreditsOn, userId);

  const usageData = usageQuery.data;
  const viewModel = useMemo(() => (usageData ? buildUsageViewModel(usageData) : null), [usageData]);
  const monthMeter = useMemo(
    () =>
      seatUsageMeters(seatPools ?? []).find(
        (meter) => meter.meterKey === 'ai-credits' && meter.userId === userId
      ),
    [seatPools, userId]
  );

  const windows = windowLimitQuery.data?.windows ?? [];
  const limitWindow = mainWindow(windows);
  const sharedCreditsUsed = Math.round(spend?.totalCredits ?? 0);
  const poolLeft = Math.round(creditSummary?.balanceCredits ?? 0);
  const canUseSharedCredits = isSharedCreditsOn && hasCreditAccess && poolLeft > 0;
  const rangeLabel = usageData ? formatWindow(usageData.period.start, usageData.period.end) : '';
  const isLoadingLimits = windowLimitQuery.isLoading || isLoadingSeats;
  const showLimitCards = (planName != null && isLoadingLimits) || !!limitWindow || !!monthMeter;

  // Wait for every figure, so the banner does not change while the panel loads.
  const notice =
    isFreeAccess || isLoadingLimits || usageQuery.isLoading
      ? null
      : pickTeamsUsageNotice(
          {
            planName,
            dailyPercent: limitWindow ? limitWindow.percent : null,
            dailyResetsAt: limitWindow?.resets_at
              ? resetTime(limitWindow.resets_at, t('TOMORROW', { defaultValue: 'tomorrow' }))
              : '',
            monthPercent: monthMeter ? meterPercent(monthMeter) : null,
            monthRenewsOn: monthMeter ? meterRenewsOn(monthMeter) : '',
            periodElapsedShare: periodElapsedShare(monthMeter),
            tokensInRange: usageData ? usageData.totals.total_tokens : null,
            rangeLabel,
            lastLogin: member.lastLogin ?? null,
            sharedCreditsUsed,
            hasCreditAccess,
            canUseSharedCredits,
          },
          t
        );

  const getSharedCreditsLabel = () => {
    const used = formatNumber(sharedCreditsUsed);
    if (!hasCreditAccess || !isSharedCreditsOn) {
      if (sharedCreditsUsed > 0) {
        return t('TEAMS_SHARED_USED_NO_ACCESS', { used, defaultValue: '{{used}} used · no access now' });
      }
      return t('TEAMS_SHARED_CREDITS_NO_ACCESS', { defaultValue: 'No access' });
    }
    if (!spend) return t('TEAMS_SHARED_CREDITS_HAS_ACCESS', { defaultValue: 'Has access' });
    return t('TEAMS_SHARED_USED_POOL_LEFT', {
      used,
      left: formatNumber(poolLeft),
      defaultValue: '{{used}} used · {{left}} left in the pool',
    });
  };

  const getNoUsageText = () => {
    if (member.lastLogin) {
      return t('TEAMS_NO_USAGE_LAST_LOGIN', {
        range: rangeLabel,
        date: member.lastLogin,
        defaultValue: 'No usage in {{range}}. Last login: {{date}}.',
      });
    }
    return t('TEAMS_NO_USAGE', { range: rangeLabel, defaultValue: 'No usage in {{range}}.' });
  };

  return (
    <div className="flex flex-col gap-4">
      {notice && <TeamsUsageNotice notice={notice} />}

      {isFreeAccess ? (
        <UsageUnlimitedNotice />
      ) : (
        <>
          {showLimitCards && (
            <div className="grid gap-4 sm:grid-cols-2">
              {windowLimitQuery.isLoading ? (
                <Skeleton className="h-52 w-full rounded-section" />
              ) : (
                <TeamsTodayCard windows={windows} />
              )}
              {isLoadingSeats && <Skeleton className="h-52 w-full rounded-section" />}
              {!isLoadingSeats && monthMeter && <TeamsMonthCard meter={monthMeter} />}
            </div>
          )}
          <Card className={panelCardClassName}>
            <CardContent className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-5 py-4">
              <h3 className="text-sm font-medium text-foreground">
                {t('USAGE_SHARED_CREDITS', { defaultValue: 'Shared credits' })}
              </h3>
              <span className="text-sm text-grey-600 dark:text-grey-300">{getSharedCreditsLabel()}</span>
            </CardContent>
          </Card>
        </>
      )}

      {usageQuery.isLoading && (
        <>
          <Skeleton className="h-96 w-full rounded-section" />
          <Skeleton className="h-80 w-full rounded-section" />
        </>
      )}

      {usageQuery.isError && (
        <UsageStateCard
          icon={<AlertCircle className="h-5 w-5" />}
          title={t('DATA_UNAVAILABLE')}
          description={t('LOAD_FAILED')}
        />
      )}

      {!usageQuery.isLoading && !usageQuery.isError && !usageData && (
        <UsageStateCard
          icon={<AlertCircle className="h-5 w-5" />}
          title={t('NO_DATA')}
          description={t('NO_DATA_FOR_RANGE')}
        />
      )}

      {usageData && usageData.totals.total_tokens === 0 && (
        <Card className={panelCardClassName}>
          <CardContent className="px-5 py-8 text-center text-sm text-grey-600 dark:text-grey-300">
            {getNoUsageText()}
          </CardContent>
        </Card>
      )}

      {usageData && viewModel && usageData.totals.total_tokens > 0 && (
        <>
          <UsageDailySection days={viewModel.allDays} peakTokens={viewModel.peakTokens} />
          <UsageWhoUsedCard
            compact
            models={viewModel.orderedModels}
            agents={viewModel.byAgent}
            users={[]}
            showUsers={false}
            totalTokens={usageData.totals.total_tokens}
          />
          <UsageActivityCard
            totals={usageData.totals}
            dateRange={rangeLabel}
            periodDays={viewModel.periodDays.length}
            activeDays={usageData.breakdowns?.daily?.length ?? 0}
            promptShare={viewModel.promptShare}
            completionShare={viewModel.completionShare}
            completionRatio={viewModel.completionRatio}
          />
        </>
      )}
    </div>
  );
}
